import { memo } from 'react'
import { SxProps } from '@mui/material'
import dayjs from 'dayjs'
import clsx from 'clsx'
import isBetweenPlugin from 'dayjs/plugin/isBetween'
import isSameOrAfterPlugin from 'dayjs/plugin/isSameOrAfter'
import isSameOrBeforePlugin from 'dayjs/plugin/isSameOrBefore'
import weekdayPlugin from 'dayjs/plugin/weekday'
import localeDataPlugin from 'dayjs/plugin/localeData'

import { formatDate } from '@/utils'
import { Text } from '@/components/Data'
import { Box } from '@/components/Layout'
import { dateRangePickerStyles as styles } from './DateRangePicker.style'
import DateRangePickerDay from './DateRangePickerDay'
import { DateRangePickerProps } from './DateRangePicker.types'
import { useDateRangePickerController } from './DateRangePicker.controller'
import DateRangePickerHeader from './DateRangePickerHeader/DateRangePickerHeader'
import DateRangePickerYears from './DateRangePickerYears/DateRangePickerYears'
import DateRangePickerMonths from './DateRangePickerMonths/DateRangePickerMonths'
import { dateRangePickerClasses } from './DateRangePicker.data'

// Extend dayjs once in this module
dayjs.extend(isBetweenPlugin)
dayjs.extend(isSameOrAfterPlugin)
dayjs.extend(isSameOrBeforePlugin)
dayjs.extend(weekdayPlugin)
dayjs.extend(localeDataPlugin)

function DateRangePicker({
  value,
  onChange,
  dotDate,
  labels = [],
  disablePastDates = false,
  disableFutureDates = false,
  minDate,
  maxDate,
  defaultCalendarMonth,
  singleDate = false,
  sx,
  headerProps = {},
  bodyProps = {},
  weeksWrapperProps = {},
  weekTextProps = {},
  daysWrapperProps = {},
  dayProps = {},
  yearPickerProps = {},
  monthPickerProps = {},
  fullWidth = true,
  className = '',
  ...rest
}: DateRangePickerProps) {
  const {
    startDate,
    endDate,
    onDateSelect,
    hoverDate,
    setHoverDate,
    currentMonth,
    onPrevMonth,
    onNextMonth,
    weekDays,
    days,
    labelsMap,
    showYearSelect,
    onShowYearSelector,
    onYearSelect,
    showMonthSelect,
    onMonthSelect,
  } = useDateRangePickerController({
    value,
    onChange,
    labels,
    singleDate,
    defaultCalendarMonth,
  })

  // props extractions
  const { sx: bodySx, ...otherBodyProps } = bodyProps
  const { sx: weeksWrapperSx, ...otherWeeksWrapperProps } = weeksWrapperProps
  const { sx: weekTextSx, ...otherWeekTextProps } = weekTextProps
  const { sx: daysWrapperSx, ...otherDaysWrapperProps } = daysWrapperProps

  const showDays = !showYearSelect && !showMonthSelect

  return (
    <Box
      sx={[styles.wrapper, sx] as SxProps}
      className={clsx({
        [dateRangePickerClasses.fullWidth]: fullWidth,
        [className]: Boolean(className),
      })}
      {...rest}>
      <DateRangePickerHeader
        currentMonth={currentMonth}
        onPrevMonth={onPrevMonth}
        onNextMonth={onNextMonth}
        disablePastDates={disablePastDates}
        disableFutureDates={disableFutureDates}
        minDate={minDate}
        maxDate={maxDate}
        onHeaderClick={onShowYearSelector}
        {...headerProps}
      />

      <Box sx={[styles.body, bodySx] as SxProps} {...otherBodyProps}>
        {showDays && (
          <>
            <Box sx={[styles.weeksWrapper, weeksWrapperSx] as SxProps} {...otherWeeksWrapperProps}>
              {weekDays.map((week) => (
                <Text
                  key={week}
                  sx={[styles.weekDay, weekTextSx] as SxProps}
                  {...otherWeekTextProps}>
                  {week}
                </Text>
              ))}
            </Box>
            <Box sx={[styles.daysWrapper, daysWrapperSx] as SxProps} {...otherDaysWrapperProps}>
              {days.map((date) => {
                const dateKey = formatDate(date, 'YYYY-MM-DD')
                const dayLabels = labelsMap.get(dateKey) || []
                let isDisabled = false

                if (
                  (disablePastDates && date.isBefore(dayjs(), 'day')) ||
                  (disableFutureDates && date.isAfter(dayjs(), 'day')) ||
                  (minDate && date.isBefore(dayjs(minDate), 'day')) ||
                  (maxDate && date.isAfter(dayjs(maxDate), 'day'))
                ) {
                  isDisabled = true
                }
                return (
                  <DateRangePickerDay
                    key={date.toString()}
                    date={date}
                    startDate={startDate}
                    endDate={endDate}
                    hoverDate={hoverDate}
                    isCurrentMonth={date.month() === currentMonth.month()}
                    labels={dayLabels}
                    showDot={dotDate ? date.isSame(dayjs(dotDate), 'day') : false}
                    onClick={() => {
                      onDateSelect(date)
                    }}
                    onMouseEnter={() => setHoverDate(date)}
                    onMouseLeave={() => setHoverDate(null)}
                    disabled={isDisabled}
                    disableRangeHighlight={singleDate}
                    {...dayProps}
                  />
                )
              })}
            </Box>
          </>
        )}

        {showYearSelect && (
          <DateRangePickerYears
            date={currentMonth}
            disablePastDates={disablePastDates}
            disableFutureDates={disableFutureDates}
            minDate={minDate}
            maxDate={maxDate}
            onYearSelect={onYearSelect}
            {...yearPickerProps}
          />
        )}

        {showMonthSelect && (
          <DateRangePickerMonths
            date={currentMonth}
            disablePastDates={disablePastDates}
            disableFutureDates={disableFutureDates}
            minDate={minDate}
            maxDate={maxDate}
            onMonthSelect={onMonthSelect}
            {...monthPickerProps}
          />
        )}
      </Box>
    </Box>
  )
}

export default memo(DateRangePicker)
